/**
 * LL33 card (C7 contract, wf_ll33fe/c7_contract.md). Types are the camelized
 * wire. `persist: false`: preview URLs expire in 15 min and must never
 * rehydrate, so CACHE_SCHEMA_VERSION needs no bump. A 404 (endpoint not
 * deployed, or not this company's building) reads as no card, never fake data.
 */
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import api, { IMPERSONATION_HEADER, impersonationHeaders } from "@/api/base";
import { httpStatusOf } from "@/components/portal/httpStatus";
import { rereadActionPlan } from "@/components/portal/useActionPlanFeed";
import type { Ll33Authorization, RepActivityGrant } from "./useLl33Rep";

export type StepKey =
  | "authorize"
  | "supers"
  | "labels"
  | "doors"
  | "links"
  | "posted"
  | "deadline";
export type Who = "you" | "codeclear" | "super" | "dob";
export type StepState = "done" | "due" | "late" | "waiting" | "locked";
export type NextAction =
  | "authorize"
  | "add_super"
  | "download_labels"
  | "count_doors"
  | "send_links"
  | "post_photos"
  | "check_photos"
  | "keep_posted"
  | "none";
export type BuildingState =
  | "not_covered"
  | "not_required"
  | "waiting_on_count"
  | "count_low"
  | "posting"
  | "posted"
  | "not_on_record";
export type Pip = "posted" | "checking" | "open" | "estimate";
export type PartyRole = "super" | "community_manager" | "compliance_lead";
export type BlockedOn = "you" | "dob";
export type RetakeState =
  | "sent"
  | "held"
  | "not_needed"
  | "queued"
  | "off"
  | "no_link";

export interface Ll33Step {
  key: StepKey;
  who: Who;
  state: StepState;
  waitingOn: StepKey | "dob" | null;
  dueOn: string | null;
  dueSource: "proposed" | "set" | "own" | "law" | "none";
  lateDays: number;
  lateFrom: string | null;
  doneOn: string | null;
  editable: boolean;
  minOn: string | null;
  maxOn: string | null;
  /** Card only, Labels only. */
  recheckOn: string | null;
}

export interface Ll33Next {
  step: StepKey | null;
  who: Who;
  action: NextAction;
  dueOn: string | null;
  lateDays: number;
}

export interface Ll33StatusLine {
  state: BuildingState;
  posted: number;
  checking: number;
  denominator: number;
  pips: Pip[];
  next: Ll33Next;
  late: boolean;
  blocked: BlockedOn | null;
}

export interface Ll33Photo {
  id: string;
  receivedAt: string;
  via: "link" | "email" | "user";
  sender: string;
  state: "counted" | "checking" | "not_counted";
  notCountedReason:
    | "rejected"
    | "before_label"
    | "link_revoked"
    | "door_retired"
    | "unassigned"
    | "closeup"
    | "reopened"
    | null;
  rejectedReason: string | null;
  claims: {
    takenAt: string | null;
    lat: string | null;
    lon: string | null;
    sentAt: string | null;
  };
  sha256: string;
  contentType: string;
  preview: { url: string; expiresAt: string } | null;
  addedBy: string | null;
  approvedAt: string | null;
  retake: { state: RetakeState; at: string | null } | null;
}

export interface Ll33Door {
  id: string;
  label: string;
  streetNumber: string;
  bin: string | null;
  labelReady: boolean;
  state: "posted" | "checking" | "not_posted" | "retired";
  postedAt: string | null;
  confirmedOn: string | null;
  link: {
    state: "active" | "revoked" | "expired" | "none";
    issuedOn: string | null;
    sentTo: PartyRole | null;
    reason: "first" | "change" | "reopen" | "retake" | null;
  };
  photos: Ll33Photo[];
}

export interface Ll33Person {
  partyId: string;
  role: PartyRole;
  name: string;
  email: string;
  phone: string;
}

export interface Ll33DoorCount {
  confirmed: number;
  estimateLow: number | null;
  estimateHigh: number | null;
  looksLow: boolean;
  countedOn: string | null;
  countedBy: Who | null;
  byBin: {
    bin: string;
    count: number;
    countMin: number;
    countMax: number | null;
  }[];
}

/** Shared by the card and every board row. */
export interface Ll33Remind {
  lastRemindedAt: string | null;
  nextReminderAt: string | null;
  remindedTodayAt: string | null;
  canRemindNow: boolean;
}

export interface Ll33Reminders {
  daysBefore: number;
  everyDays: number;
  cmAfterDays: number;
  leadOn: string;
}

export interface Ll33Card extends Ll33Remind {
  buildingId: string;
  address: string;
  bin: string;
  periodKey: string;
  asOf: string;
  complianceMatterId: string | null;
  covered: boolean;
  notCoveredReason: string | null;
  notCoveredSource: string | null;
  inService: boolean;
  legalDeadline: string;
  statusLine: Ll33StatusLine;
  steps: Ll33Step[];
  doorCount: Ll33DoorCount;
  escalation: { role: PartyRole; on: string; sentAt: string | null }[];
  labels: { bin: string; grade: string | null; downloadedOn: string | null }[];
  doors: Ll33Door[];
  unassignedPhotos: Ll33Photo[];
  /** Null while the ll33_reminder_send switch or its LL33 master is off. */
  reminders: Ll33Reminders | null;
  people: Ll33Person[];
  maxProofBytes: number;
  activity: {
    at: string;
    kind: string;
    doorId: string | null;
    by: Who;
    step: StepKey | null;
    dueOn: string | null;
    grant: RepActivityGrant | null;
    party: { role: PartyRole; name: string | null } | null;
    count: number | null;
  }[];
  thread: { lawCode: "LL33"; periodKey: string; enabled: boolean };
  authorization: Ll33Authorization;
  /** The company's (R1), as the board serves it. */
  firstRun: boolean;
  /** The board row's; null when not covered or no BIN. */
  authorized: boolean | null;
}

export { dateErrorsOf, type DateError, type DateErrorCode } from "./dateErrors";

/** The label year is New York's calendar year. */
export const ll33PeriodKey = (now: Date = new Date()): string =>
  new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    timeZone: "America/New_York",
  }).format(now);

export const ll33QueryKey = (buildingId: string, periodKey: string) =>
  ["building", buildingId, "ll33", periodKey] as const;

export const REP_LIST_KEY = ["ll33-rep-list"] as const;

/** Every building's card query (ll33QueryKey), for writes that reach many buildings. */
export const isLl33CardQuery = (q: { queryKey: readonly unknown[] }) => {
  const [root, , law] = ll33QueryKey("", "");
  return q.queryKey[0] === root && q.queryKey[2] === law;
};

export const cardPath = (buildingId: string, periodKey: string) =>
  `tracking/portal/buildings/${buildingId}/ll33/${periodKey}/`;

export { IMPERSONATION_HEADER };

/** Every LL33 read and write, so staff read the dates their writes return (R1 audience b). */
export const ll33Headers = impersonationHeaders;

/** The one request every LL33 write and undo sends. */
export const ll33Write = <T = unknown>(
  method: "POST" | "PATCH",
  url: string,
  data: unknown,
) => api.request<T>({ method, url, data, headers: ll33Headers() });

/** Absolute, for `downloadAuthedFile` (fetch, not axios). */
export const ll33OriginalUrl = (card: Ll33Card, photoId: string): string =>
  `${api.defaults.baseURL ?? "/api/"}${cardPath(card.buildingId, card.periodKey)}files/${photoId}/original/`;

export type PreviewUrl = NonNullable<Ll33Photo["preview"]>;

/** Refetch once per failed URL, only once it has expired (15 min); a fresh URL that fails would loop. */
export const usePreviewRetry = (card: Ll33Card) => {
  const client = useQueryClient();
  const tried = React.useRef(new Set<string>());
  return (preview: PreviewUrl) => {
    if (tried.current.has(preview.url)) return;
    if (Date.parse(preview.expiresAt) > Date.now()) return;
    tried.current.add(preview.url);
    client.invalidateQueries({
      queryKey: ll33QueryKey(card.buildingId, card.periodKey),
    });
  };
};

/** One card read: useLl33, and the rail's Authorize reading one card per owner (useQueries). */
export const ll33CardQuery = (
  buildingId: string,
  periodKey: string,
  enabled = true,
) =>
  ({
    queryKey: ll33QueryKey(buildingId, periodKey),
    enabled: enabled && buildingId.length > 0,
    queryFn: async (): Promise<Ll33Card | null> => {
      try {
        return (
          await api.get<Ll33Card>(cardPath(buildingId, periodKey), {
            headers: ll33Headers(),
          })
        ).data;
      } catch (err) {
        if (httpStatusOf(err) === 404) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
    meta: { persist: false },
  }) as const;

/** `enabled` false: no request (the code is not In service). */
export const useLl33 = (
  buildingId: string,
  enabled = true,
  periodKey: string = ll33PeriodKey(),
) => useQuery(ll33CardQuery(buildingId, periodKey, enabled));

/** All or nothing; 200 is the full card, 400 is `{errors: DateError[]}`. No Undo is served. */
export const useLl33Dates = (card: Ll33Card) => {
  const client = useQueryClient();
  return useMutation<Ll33Card, unknown, { step: StepKey; dueOn: string }[]>({
    mutationFn: async (dates) =>
      (
        await ll33Write<Ll33Card>(
          "PATCH",
          `${cardPath(card.buildingId, card.periodKey)}dates/`,
          { dates },
        )
      ).data,
    onSuccess: (next) =>
      client.setQueryData(ll33QueryKey(card.buildingId, card.periodKey), next),
    onSettled: () => rereadActionPlan(client),
  });
};
